import * as React from "react";
import { AppBody } from "../AppBody";
import { Query, SearchPillData, SortDirection } from "../../interfaces";
import { Grid, Typography } from "@mui/material";
import StructureChangeForm from "./StructureChangeForm";
import {
  appHeaderTitle,
  appTitles,
  formatDate,
  getExpandState,
} from "../../common/services/Utilities";
import LinkComponent from "../LinkComponent";
import { Switch, Route, Redirect } from "react-router-dom";
import { structureChangeLevelTwoPathingMap } from "../../refdata/constants";
import { eventCode } from "../../refdata/FilterOptions";
import {
  StructureChangeLevelOne,
  StructureChangeLevelTwo,
} from "../../containers/StructureChangeLevelContainers";
import useSortProperties from "../../hooks/useSortProperties";
import Breadcrumbs from "../../containers/Breadcrumbs";
import BackButton from "../../containers/BackButton";
import HelpButton from "../HelpContent/HelpButton";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";

interface Props {
  appState: Query;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  resetState: () => void;
}

const StructureChangeIntro = (tableLevel: number) => (
  <>
    {appHeaderTitle("oscr")}
    {tableLevel !== 2 ? (
      <div id="intro-paragraph">
        <Typography variant="body2">
          The Events & Changes search of BankFind Suite allows you to find
          structural, non-financial activity for a specific date or date range,
          and reflects transactions by either the processed date or the
          effective date.
        </Typography>
        <LinkComponent appName={"EventsChanges"} />
      </div>
    ) : (
      undefined
    )}
  </>
);

export function StructureChange({ appState, updateSort, resetState }: Props) {
  const oscrSearchPillData: SearchPillData[] = [
    {
      label: "Name",
      value: appState.search.name,
      formKey: "name",
      search: true,
    },
    {
      value: appState.search.certSearch,
      search: true,
      formKey: "certSearch",
      label: "Cert",
    },
    {
      value: formatDate(appState.filters.startDate),
      formKey: "startDate",
      static: true,
      label: "Start Date",
      search: false,
    },
    {
      value: formatDate(appState.filters.endDate),
      formKey: "endDate",
      static: true,
      label: "End Date",
      search: false,
    },
    {
      value:
        appState.filters.searchDateRadio === "PROCDATE"
          ? "Processed Dates"
          : "",
      formKey: "searchDateRadio",
      static: true,
      search: false,
    },
    {
      value:
        appState.filters.searchDateRadio === "EFFDATE" ? "Effective Dates" : "",
      formKey: "searchDateRadio",
      static: true,
      search: false,
    },
    {
      value: appState.search.stateSearch,
      formKey: "stateSearch",
      label: "State",
      search: true,
    },
    {
      value: appState.search.city,
      formKey: "city",
      label: "City",
      search: true,
    },
  ];

  const arrayOfTitles = eventCode.map(event => event.label);

  const isTableLevelOne =
    window.location.pathname
      .split("/")
      .filter((path: string) => path !== "")
      .pop() === "oscr";

  React.useEffect(() => {
    //resets state to default when navigating to URL with no query params
    if (!window.location.search) {
      resetState();
    }
  }, [resetState]);

  const sortProperties = useSortProperties(appState, updateSort);
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={11}>
          <Breadcrumbs currentPageBreadCrumbTitle={appTitles("oscr")} />
        </Grid>
        <Grid item xs={1}>
          <HelpButton />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={StructureChangeIntro(isTableLevelOne ? 1 : 2)}
        backButton={
          isTableLevelOne ? (
            undefined
          ) : (
            <BackButton
              app="OSCR"
              buttonLabel="Back to Summary"
              variant="dark"
            />
          )
        }
        searchForm={
          <StructureChangeForm
            appState={appState}
            tableLevel={isTableLevelOne ? 1 : 2}
          />
        }
        expand={getExpandState(appState.ui.searchPanelExpand, true)}
      >
        <Switch>
          <Route
            exact
            path={`/oscr/:eventType`}
            render={(match: any) => {
              let eventType = match.location.pathname.split("/")[2];
              eventType = eventType
                ? structureChangeLevelTwoPathingMap[eventType.split("?")[0]]
                : eventType;
              return arrayOfTitles.includes(eventType) ? (
                <StructureChangeLevelTwo
                  oscrSearchPillData={oscrSearchPillData}
                  sortProperties={sortProperties}
                />
              ) : (
                <Redirect to={{ pathname: "/" }} />
              );
            }}
          />
          <Route
            exact
            path={`/oscr`}
            render={() => {
              return (
                <StructureChangeLevelOne
                  oscrSearchPillData={oscrSearchPillData}
                  sortProperties={sortProperties}
                />
              );
            }}
          />
        </Switch>
      </AppBody>
    </>
  );
}
